import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang, ui, tx } from "../i18n";
import { Icon } from "./Icon";
import { ReadinessCard } from "./ReadinessCard";
import { ShoppingList } from "./ShoppingList";
import { CHECKLISTS } from "../data/checklists";

const STORE_KEY = "ppr-checklists-v1";

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch { return {}; }
}

// Texto para compartir la checklist por WhatsApp: progreso + artículos FALTANTES (para coordinar suministros)
function waChecklistText(checklist, checkedList, lang) {
  const missing = checklist.items
    .map((item, i) => ({ item, i }))
    .filter(({ i }) => !checkedList.includes(i))
    .slice(0, 15);
  const done = checkedList.length;
  const total = checklist.items.length;
  const title = lang === "es" ? checklist.title.es : checklist.title.en;
  if (missing.length === 0) {
    return lang === "es"
      ? `CHECKLIST COMPLETA — ${title}\n${total}/${total} artículos listos.\n\npreparatepr.org — Puerto Rico, Preparado Siempre`
      : `CHECKLIST COMPLETE — ${title}\n${total}/${total} items ready.\n\npreparatepr.org — Puerto Rico, Always Prepared`;
  }
  const lines = missing.map(({ item }) => `• ${lang === "es" ? item.es : item.en}`).join("\n");
  const more = checklist.items.length - done - missing.length;
  return lang === "es"
    ? `CHECKLIST FAMILIAR — ${title}\nProgreso: ${done}/${total} artículos listos\n\nFALTAN:\n${lines}${more > 0 ? `\n…y ${more} más` : ""}\n\nCoordina los suministros en familia — preparatepr.org`
    : `FAMILY CHECKLIST — ${title}\nProgress: ${done}/${total} items ready\n\nMISSING:\n${lines}${more > 0 ? `\n…and ${more} more` : ""}\n\nCoordinate supplies as a family — preparatepr.org`;
}

export function ChecklistsTab({ lowBandwidth }) {
  const { lang } = useLang();
  const [activeId, setActiveId] = useState(CHECKLISTS[0].id);
  const [checked, setChecked] = useState(loadState);

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(checked)); } catch {}
  }, [checked]);

  const active = CHECKLISTS.find((c) => c.id === activeId);
  const activeChecked = checked[activeId] || [];
  const pct = Math.round((activeChecked.length / active.items.length) * 100);

  const toggle = (id, idx) => {
    setChecked((prev) => {
      const list = prev[id] || [];
      const next = list.includes(idx) ? list.filter((i) => i !== idx) : [...list, idx];
      return { ...prev, [id]: next };
    });
  };

  const reset = () => setChecked((prev) => ({ ...prev, [activeId]: [] }));

  return (
    <div data-testid="checklists-tab" className="px-4 sm:px-8 lg:px-12 py-8 sm:py-10">
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{tx(ui.checklists.title, lang)}</h2>
      <p className="mt-1 mb-6 text-sm sm:text-base text-slate-600">{tx(ui.checklists.subtitle, lang)}</p>

      <ReadinessCard checked={checked} />

      <div className="no-print flex gap-2 overflow-x-auto no-scrollbar mb-6">
        {CHECKLISTS.map((c) => {
          const list = checked[c.id] || [];
          const p = Math.round((list.length / c.items.length) * 100);
          const isActive = c.id === activeId;
          return (
            <button
              key={c.id}
              data-testid={`checklist-select-${c.id}`}
              onClick={() => setActiveId(c.id)}
              className={`whitespace-nowrap flex items-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${
                isActive ? "border-amber-600 bg-amber-50 text-[#1C2B4A]" : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"
              }`}
            >
              <Icon name={c.icon} className={`w-4 h-4 ${isActive ? "text-amber-700" : ""}`} />
              {tx(c.title, lang)}
              <span className={`font-mono-pr text-[11px] ${p === 100 ? "text-green-700" : "text-slate-400"}`}>{p}%</span>
            </button>
          );
        })}
      </div>

      <div className="print-area rounded-lg border border-slate-300 bg-white p-6 sm:p-8 max-w-4xl">
        <div className="print-only mb-4 flex items-center gap-3 border-b-2 border-slate-800 pb-3">
          <img src="/logo.png" alt="" className="w-12 h-12" />
          <div>
            <p className="font-display text-lg font-black tracking-tight">preparatepr<span className="text-amber-600">.org</span></p>
            <p className="text-xs text-slate-500">{tx(ui.checklists.printTitle, lang)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-11 h-11 rounded-md bg-[#1C2B4A] text-white">
              <Icon name={active.icon} className="w-5 h-5" />
            </span>
            <div>
              <h3 data-testid="active-checklist-title" className="font-display text-xl sm:text-2xl font-bold text-slate-900">{tx(active.title, lang)}</h3>
              <p className="text-xs text-slate-500">{tx(ui.checklists.saved, lang)} · preparatepr.org</p>
            </div>
          </div>
          <div className="no-print flex gap-2">
            <a
              data-testid="checklist-share-wa"
              href={`https://wa.me/?text=${encodeURIComponent(waChecklistText(active, activeChecked, lang))}`}
              target="_blank"
              rel="noopener noreferrer"
              title={lang === "es" ? "Compartir progreso y artículos faltantes por WhatsApp" : "Share progress and missing items on WhatsApp"}
              className="inline-flex items-center gap-1.5 rounded-md border-2 border-green-700 px-3.5 py-2 text-sm font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-200 active:scale-[0.98]"
            >
              <Icon name="MessageCircle" className="w-4 h-4" /> WhatsApp
            </a>
            <button
              data-testid="checklist-reset-btn"
              onClick={reset}
              className="inline-flex items-center gap-1.5 rounded-md border-2 border-slate-300 px-3.5 py-2 text-sm font-bold text-slate-600 hover:border-slate-500 transition-colors duration-200 active:scale-[0.98]"
            >
              <Icon name="RotateCcw" className="w-4 h-4" /> {tx(ui.checklists.reset, lang)}
            </button>
            <button
              data-testid="checklist-print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1C2B4A] px-3.5 py-2 text-sm font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98]"
            >
              <Icon name="Printer" className="w-4 h-4" /> {tx(ui.checklists.print, lang)}
            </button>
          </div>
        </div>

        <div className="no-print mb-6">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-bold text-slate-700">{tx(ui.checklists.completed, lang)}</span>
            <span data-testid="checklist-progress-label" className={`font-mono-pr text-sm font-bold ${pct === 100 ? "text-green-700" : "text-amber-700"}`}>{pct}%</span>
          </div>
          <div data-testid="checklist-progress-bar" className="h-3 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100">
            <motion.div
              className={`h-full rounded-full ${pct === 100 ? "bg-green-700" : "bg-amber-600"}`}
              initial={false}
              animate={{ width: `${pct}%` }}
              transition={lowBandwidth ? { duration: 0 } : { duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>

        <ul className="divide-y divide-slate-200">
          {active.items.map((item, idx) => {
            const isChecked = activeChecked.includes(idx);
            return (
              <li key={idx}>
                <label
                  data-testid={`checklist-item-${activeId}-${idx}`}
                  className="flex cursor-pointer items-start gap-3.5 py-3.5 group"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(activeId, idx)}
                    className="peer sr-only"
                  />
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors duration-150 ${
                    isChecked ? "border-green-700 bg-green-700 text-white" : "border-slate-400 bg-white group-hover:border-amber-600"
                  }`}>
                    {isChecked && <Icon name="CheckCircle2" className="w-4 h-4" strokeWidth={3} />}
                  </span>
                  <span className={`text-base leading-relaxed transition-colors duration-150 ${isChecked ? "text-slate-400 line-through" : "text-slate-800"}`}>
                    {tx(item, lang)}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <ShoppingList checked={checked} onToggle={toggle} />
    </div>
  );
}
